import { Box, Typography, Paper, Button, IconButton, Chip, CircularProgress, useTheme } from '@mui/material';
import { useFormContext, useFieldArray, Controller } from 'react-hook-form';
import Input from '@/components/Common/Input';
import { RiDeleteBin6Line } from 'react-icons/ri';
import { FiUpload, FiTrash2, FiPlus, FiDownload } from 'react-icons/fi';
import Cookies from 'js-cookie';
import { useEffect, useState } from 'react';
import {
  useCreateOrUpdateAgencyOnboardingForm,
  useDeleteOnboardingFile,
  useUpdatePublicOnboardingForm
} from '@/apis/agency/businessOnboarding/hooks';

export default function FAQsForm() {
  const theme = useTheme();
  const { control, watch, setValue } = useFormContext();
  const currentFormId = Cookies.get('onboarding_id');
  const isBusinessOnboardingStage2 = location.pathname.includes('/business-onboarding/stage-2');

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'faq'
  });

  const faqList = watch('faq') || [];
  const scrapingStatus = watch('scraping_status');
  const isFaqScrapingInProgress = scrapingStatus === 'queued';
  const answeredCount = faqList.filter((f: any) => f?.answer?.trim()).length;

  const {
    fields: kbFields,
    append: kbAppend,
    remove: kbRemove
  } = useFieldArray({
    control,
    name: 'knowledgebase_files_attributes'
  });

  const { mutate: updateAgency, isPending: isAgencyUpdating } = useCreateOrUpdateAgencyOnboardingForm(false);
  const { mutate: updatePublic, isPending: isPublicUpdating } = useUpdatePublicOnboardingForm();
  const { deleteFile } = useDeleteOnboardingFile();
  const [uploadingIndex, setUploadingIndex] = useState<number | null>(null);

  const isUploading = isAgencyUpdating || isPublicUpdating;

  // Sync upload status with form context for global footer tracking
  useEffect(() => {
    setValue('isUploadingDoc', isUploading);
    if (!isUploading) {
      setUploadingIndex(null);
    }
  }, [isUploading, setValue]);

  const updateOnboardingForm = isBusinessOnboardingStage2
    ? (payload: FormData, options?: any) => currentFormId && updatePublic({ formId: currentFormId, payload }, options)
    : (payload: FormData, options?: any) => updateAgency(payload, options);

  return (
    <Box>
      {isFaqScrapingInProgress && (
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            alignItems: 'flex-start',
            backgroundColor: theme.palette.primary.light,
            border: `1px solid ${theme.palette.primary.light}`,
            borderRadius: '8px',
            p: '12px 16px',
            mb: 2
          }}
        >
          <Box sx={{ position: 'relative', display: 'inline-flex', mt: 0.25, flexShrink: 0 }}>
            <CircularProgress
              variant='determinate'
              value={100}
              size={20}
              thickness={4}
              sx={{ color: theme.palette.primary.main, opacity: 0.2 }}
            />
            <CircularProgress size={20} thickness={4} color='primary' sx={{ position: 'absolute', left: 0 }} />
          </Box>
          <Box>
            <Typography fontWeight={600} fontSize={16}>
              Reviewing Your Website and Getting Questions Ready
            </Typography>
            <Typography fontSize={14} color='text.secondary' mt={0.5} lineHeight={1.4}>
              We&apos;re analyzing and extracting information from the website. This usually takes 4-5 minutes.
              Meanwhile, you can continue filling out other steps in the form.
            </Typography>
          </Box>
        </Box>
      )}

      {/* ================= FAQ SECTION ================= */}
      <Paper
        sx={{
          border: '1px solid #EAEAEA',
          borderRadius: 2,
          p: { xs: 2, md: 4 },
          opacity: isFaqScrapingInProgress ? 0.5 : 1,
          pointerEvents: isFaqScrapingInProgress ? 'none' : 'auto'
        }}
      >
        <Typography fontWeight={600} fontSize={18}>
          Frequently Asked Questions
        </Typography>

        <Typography variant='body2' color='text.secondary' mt={1}>
          These FAQs will be used by your AI agent to answer customer calls. The information you add here becomes part
          of the agent’s knowledge base, helping it respond accurately to common questions.
        </Typography>

        <Box
          sx={{
            backgroundColor: '#FFF4E5', // light orange background
            borderRadius: '8px',
            padding: '12px 16px',
            border: '1px solid #FFE0B2',
            mt: 2
          }}
        >
          <Typography
            sx={{
              color: '#C77700', // darker orange text
              fontSize: '14px'
            }}
          >
            <strong>Tip:</strong> The more FAQs you complete with accurate answers, the better your agent can serve your
            customers. Remove any questions that don’t apply to your business.
          </Typography>
        </Box>

        {/* LIST */}
        <Box mt={4}>
          {fields.map((field, index) => (
            <Paper key={field.id} variant='outlined' sx={{ p: { xs: 2, md: 3 }, mb: 3, borderRadius: 2 }}>
              {/* TOP ROW: Number, Question, Delete */}
              <Box display='flex' gap={2} alignItems={{ xs: 'flex-start', md: 'center' }}>
                {/* NUMBER (Circle for desktop, plain text for mobile to match image) */}
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    bgcolor: '#EEF3FF',
                    display: { xs: 'none', md: 'flex' },
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 600,
                    flexShrink: 0
                  }}
                >
                  {index + 1}
                </Box>
                <Typography
                  sx={{
                    display: { xs: 'block', md: 'none' },
                    color: 'primary.main',
                    fontWeight: 600,
                    fontSize: 18,
                    mt: 1.5
                  }}
                >
                  {index + 1}
                </Typography>

                {/* CONTENT */}
                <Box flex={1}>
                  <Input
                    name={`faq.${index}.question`}
                    control={control}
                    fullWidth
                    disabled={isFaqScrapingInProgress}
                    placeholder='Enter question'
                    InputProps={{
                      endAdornment: faqList[index]?.label ? (
                        <Chip
                          label={faqList[index].label}
                          sx={{
                            display: { xs: 'none', md: 'flex' },
                            bgcolor:
                              faqList[index].label === 'Custom'
                                ? '#F5DFFF'
                                : faqList[index].label === 'Website'
                                  ? '#FFF3E5'
                                  : '#FAFAFA',
                            color:
                              faqList[index].label === 'Custom'
                                ? '#7C00B1'
                                : faqList[index].label === 'Website'
                                  ? '#FB8703'
                                  : '#777777',
                            fontWeight: 500,
                            fontSize: '12px',
                            height: 28,
                            borderRadius: 100
                          }}
                        />
                      ) : undefined
                    }}
                  />

                  {/* MOBILE CHIP (Outside input) */}
                  {faqList[index]?.label && (
                    <Box mt={1} display={{ xs: 'block', md: 'none' }}>
                      <Chip
                        label={faqList[index].label}
                        sx={{
                          bgcolor:
                            faqList[index].label === 'Custom'
                              ? '#F5DFFF'
                              : faqList[index].label === 'Website'
                                ? '#FFF3E5'
                                : '#FAFAFA',
                          color:
                            faqList[index].label === 'Custom'
                              ? '#7C00B1'
                              : faqList[index].label === 'Website'
                                ? '#FB8703'
                                : '#777777',
                          fontWeight: 500,
                          fontSize: '12px',
                          height: 28,
                          borderRadius: 100
                        }}
                      />
                    </Box>
                  )}
                </Box>

                {/* DELETE */}
                <IconButton
                  disabled={isFaqScrapingInProgress}
                  onClick={() => remove(index)}
                  sx={{ mt: { xs: 0.5, md: 0 } }}
                >
                  <RiDeleteBin6Line color='#E53935' />
                </IconButton>
              </Box>

              {/* BOTTOM ROW: Answer (Full Width) */}
              <Box mt={2}>
                <Input
                  name={`faq.${index}.answer`}
                  control={control}
                  multiline
                  rows={4}
                  fullWidth
                  disabled={isFaqScrapingInProgress}
                  placeholder='Provide a clear and helpful answer'
                  sx={{ bgcolor: '#F4F8FF' }}
                />
              </Box>
            </Paper>
          ))}
        </Box>

        {/* FOOTER */}
        <Box mt={2} display='flex' alignItems='center' justifyContent='space-between'>
          <Button
            variant='outlined'
            disabled={isFaqScrapingInProgress}
            sx={{
              borderRadius: 20,
              textTransform: 'none',
              borderColor: '#D1D5DB'
            }}
            onClick={() => append({ question: '', answer: '', label: 'Custom' })}
          >
            + Add Custom FAQ’s
          </Button>

          <Typography variant='body2' color='text.secondary'>
            {answeredCount} of {faqList.length} answered
          </Typography>
        </Box>
      </Paper>

      {/* ================= TIP ================= */}
      <Box
        sx={{
          background: '#FFFBEB',
          borderRadius: 1,
          p: '12px',
          my: 3,
          color: '#D97706',
          fontSize: 14
        }}
      >
        Tip: The more FAQs you complete, the better your agent performs.
      </Box>

      {/* ================= KNOWLEDGEBASE FILES ================= */}
      <Paper sx={{ border: '1px solid #EAEAEA', borderRadius: 2, p: 4 }}>
        <Typography fontWeight={600} fontSize={18}>
          Additional Knowledge-base documents
        </Typography>

        <Typography variant='body2' color='text.secondary' mt={1}>
          Upload documents to improve your AI agent’s responses.
        </Typography>

        {/* LIST */}
        <Box mt={3} display='flex' flexDirection='column' gap={3}>
          {kbFields.map((field, index) => (
            <Paper key={field.id} variant='outlined' sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }}>
              {/* HEADER */}
              <Box display='flex' justifyContent='space-between'>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    bgcolor: '#EEF3FF',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 600
                  }}
                >
                  {index + 1}
                </Box>
                <Box display='flex' alignItems='center' gap={1}>
                  {watch(`knowledgebase_files_attributes.${index}.file_url`) && (
                    <IconButton
                      onClick={() => {
                        window.open(watch(`knowledgebase_files_attributes.${index}.file_url`), '_blank');
                      }}
                    >
                      <FiDownload size={20} color='#2563eb' />
                    </IconButton>
                  )}
                  <IconButton
                    onClick={() => {
                      const fileId = watch(`knowledgebase_files_attributes.${index}.id`);
                      kbRemove(index);
                      if (fileId) {
                        deleteFile(fileId, 'knowledgebase_files_attributes');
                      }
                    }}
                  >
                    <FiTrash2 color='#E53935' />
                  </IconButton>
                </Box>
              </Box>

              {/* FILE INPUT */}
              <Box mt={2}>
                <Controller
                  control={control}
                  name={`knowledgebase_files_attributes.${index}.file`}
                  render={({ field: { onChange } }) => {
                    return (
                      <>
                        <input
                          type='file'
                          hidden
                          id={`kb-file-${index}`}
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;

                            setUploadingIndex(index);
                            // ✅ Store the human-readable filename in a separate field for the UI
                            setValue(`knowledgebase_files_attributes.${index}.file_name`, file.name);

                            // ✅ Instant upload
                            const fd = new FormData();
                            fd.append('onboarding_form[knowledgebase_files_attributes][][file]', file);

                            updateOnboardingForm(fd, {
                              onSuccess: (data: any) => {
                                const files = data?.knowledgebase_files_attributes || [];
                                const uploadedFile = files[index];
                                if (uploadedFile) {
                                  setValue(`knowledgebase_files_attributes.${index}.id`, uploadedFile.id);
                                  setValue(`knowledgebase_files_attributes.${index}.file_url`, uploadedFile.file_url);
                                  // ✅ Update the 'file' field with the signed ID for the backend payload
                                  onChange(uploadedFile.file);
                                }
                                setUploadingIndex(null);
                              },
                              onError: () => {
                                setUploadingIndex(null);
                              }
                            });
                          }}
                        />

                        <label htmlFor={`kb-file-${index}`}>
                          <Paper
                            variant='outlined'
                            sx={{
                              p: 2,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 2,
                              cursor: 'pointer'
                            }}
                          >
                            {uploadingIndex === index ? <CircularProgress size={20} /> : <FiUpload size={18} />}
                            <Typography variant='body2' sx={{ wordBreak: 'break-all' }}>
                              {watch(`knowledgebase_files_attributes.${index}.file_name`) ||
                                (watch(`knowledgebase_files_attributes.${index}.file_url`)
                                  ? watch(`knowledgebase_files_attributes.${index}.file_url`)
                                      .split('/')
                                      .pop()
                                      .split('?')[0]
                                  : 'Upload document')}
                            </Typography>
                          </Paper>
                        </label>
                      </>
                    );
                  }}
                />
              </Box>

              {/* DESCRIPTION */}
              <Box mt={2}>
                <Input
                  name={`knowledgebase_files_attributes.${index}.description`}
                  control={control}
                  multiline
                  minRows={3}
                  maxRows={8}
                  fullWidth
                  placeholder='e.g. Refund policy, SOP, FAQ doc...'
                />
              </Box>
            </Paper>
          ))}
        </Box>

        {/* ADD BUTTON */}
        <Box mt={3}>
          <Button
            variant='outlined'
            startIcon={<FiPlus />}
            sx={{ borderRadius: 20 }}
            onClick={() =>
              kbAppend({
                file: null,
                description: ''
              })
            }
          >
            Add Document
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}
